import { Container, Graphics } from '../../../vendor/pixi.min.mjs';

/**
 * Continuous "sucking into the portal" confetti emitter, plus a
 * one-shot arrival flash so the portal's appearance is impossible
 * to miss:
 *
 *   1. **Continuous suction stream** — blue/white particles spawn
 *      far inside the playfield and are dragged into the portal
 *      mouth on a funnel + swirl + accelerating-suction curve.
 *      Each particle SPAWNS chunky-and-far-away, then quickly
 *      shrinks to the original "normal" particle size (~3–6.5px)
 *      and rides that size for the bulk of its travel before
 *      vanishing into the mouth. Density / swirl amplitudes are
 *      bumped vs the legacy stream so it reads as a vortex.
 *
 *   2. **One-shot arrival blast** ({@link flashOnAppear}) — fired
 *      automatically the first time {@link setPortal} switches from
 *      null → non-null geometry. Spawns ~40 radial particles flying
 *      outward + a ring centred on the mouth that starts large
 *      (blast), then implodes to ~portal-mouth size and fades out.
 *      "POP! a portal opened here", then it's gone — no persistent
 *      halo loitering on screen.
 *
 *   3. **"Go here" arrow** — small white chevron rendered just
 *      inside the playfield, pointing at the portal mouth and
 *      yo-yoing toward/away from it on a sin-wave so the player
 *      reads "move this direction to enter". Lives for the entire
 *      time a portal is set; cleared by `setPortal(null)`.
 *
 * Per-particle motion in (1) goes:
 *   • starts at a random offset along the wall (axis) up to ~portal-wide,
 *   • is pushed toward the wall on an accelerating (1 - t²) curve so the
 *     motion reads as "being inhaled" rather than "drifting",
 *   • collapses its axial offset toward 0 as it approaches the mouth so
 *     the cloud narrows from "as wide as the portal" down to a thin
 *     stream entering the gate,
 *   • carries a small sine wobble (decaying with t) for a light swirl,
 *   • shrinks fast in the first ~18% of life from chunky-spawn down to
 *     normal size, then slowly thins to ~0 at the mouth so it visibly
 *     vanishes INTO the gate instead of clipping the wall.
 *
 * Geometry passed via {@link setPortal} is a plain object:
 * ```
 * {
 *   mouthX, mouthY,         // world-space centre of the portal opening
 *   inwardDx, inwardDy,     // unit vector FROM playfield INTO the wall
 *   axisDx, axisDy,         // unit vector along the wall (perpendicular)
 *   mouthHalfWidth,         // px — half of the portal's wall-span
 * }
 * ```
 * Pass `null` (or call {@link clear}) to stop spawning new particles. The
 * already-active ones still finish their flight so the effect tapers off
 * cleanly during board transitions.
 *
 * Pooled: each finished particle's Graphics is returned to a free pool
 * and re-used on the next spawn — so a long boost trail through several
 * boards never allocates a new wave of Graphics per frame.
 */

const PALETTE = [
    0xffffff,
    0xf0f9ff,
    0xdbeafe,
    0xbfdbfe,
    0x93c5fd,
    0x60a5fa,
    0x3b82f6,
];

// Continuous-stream tuning. Per client direction — particles are
// CHUNKY at the moment they spawn (and spawn far from the mouth),
// but quickly snap down to the original "normal" particle size for
// the bulk of their travel so the stream doesn't fill the screen
// with big squares. The motion *pattern* (funnel + swirl +
// accelerating suction) is preserved — only the magnitudes change.
const MAX_PARTICLES = 96;
const EMIT_INTERVAL_FRAMES = 1.0;

// Particles spawn much farther from the mouth than the legacy
// stream so the "coming from far away" depth is readable before
// they get pulled in.
const SPAWN_DIST_MIN = 170;
const SPAWN_DIST_MAX = 240;

const LIFETIME_FRAMES_MIN = 38;
const LIFETIME_FRAMES_MAX = 58;

/** Initial size when the particle first appears at SPAWN_DIST.
 *  Big enough to read clearly across a 240px gap. Particles drop
 *  to NORMAL_SIZE within the first ~18% of their lifetime. */
const SIZE_START_MIN = 14;
const SIZE_START_MAX = 26;
/** "Normal" particle size for the bulk of the lifetime — matches
 *  the legacy SIZE_MIN/SIZE_MAX values so the suction stream looks
 *  like the original tight cloud once particles get past the
 *  initial snap-down. */
const SIZE_NORMAL_MIN = 3;
const SIZE_NORMAL_MAX = 6.5;
/** Where the size curve transitions from "fast snap to normal" to
 *  "slow shrink to vanish". 0.18 ≈ 11 frames at a 60-frame
 *  lifetime, so the chunky-to-normal moment reads as a quick
 *  "pulled into focus" beat without flickering. */
const SIZE_SNAP_T = 0.18;
/** Final size as the particle disappears into the mouth. */
const SIZE_END = 0.45;

const SWIRL_TURNS_MIN = 0.35;
const SWIRL_TURNS_MAX = 1.1;
// Wider swirls so the cloud reads as "swirling vortex" rather than a
// straight stream.
const SWIRL_AMP_MIN = 14;
const SWIRL_AMP_MAX = 32;

const SPREAD_FACTOR = 1.05;

// ── One-shot arrival blast ────────────────────────────────
// Fired the first time `setPortal` switches from null → non-null
// geometry. A burst of radial particles + a ring centred on the
// mouth that BLASTS large then shrinks down and disappears,
// signalling "POP! a portal opened here" without leaving any
// persistent halo on screen.
const FLASH_PARTICLE_COUNT     = 40;
const FLASH_SIZE_MIN           = 22;
const FLASH_SIZE_MAX           = 40;
/** Frames the particles take to fade out (~800ms at 60fps). */
const FLASH_LIFETIME_FRAMES    = 48;
/** Outward distance each particle travels from the mouth, in px. */
const FLASH_TRAVEL_MIN         = 80;
const FLASH_TRAVEL_MAX         = 160;
/** Frames the contracting ring fades over (~700ms). */
const RING_LIFETIME_FRAMES     = 42;
/** Ring starts at this multiple of `mouthHalfWidth` — the "blast"
 *  radius at t=0. */
const RING_BLAST_RADIUS_MULT   = 4.0;
/** Ring shrinks down to this multiple of `mouthHalfWidth` — close
 *  to the actual portal mouth so it reads as "the energy collapsed
 *  into the portal". */
const RING_FINAL_RADIUS_MULT   = 0.9;
const RING_START_ALPHA         = 0.9;
const RING_LINE_WIDTH          = 6;

// ── "Go here" arrow ───────────────────────────────────────
// Small chevron rendered just inside the playfield that points at
// the portal and yo-yos toward/away from it. Always visible while a
// portal is set; communicates "move this direction to enter" to
// players who might otherwise miss the gate against a busy
// background. Drawn ABOVE the suction stream so the chevron stays
// readable through the cloud.
/** Distance from the mouth (in px, along the OUTWARD normal) where
 *  the arrow sits at the FAR end of its yo-yo. */
const ARROW_FAR_OFFSET     = 90;
/** Distance from the mouth at the NEAR end of the yo-yo. The
 *  chevron travels between FAR_OFFSET and NEAR_OFFSET on a sin
 *  wave, getting visibly closer to the gate at the peak. */
const ARROW_NEAR_OFFSET    = 40;
/** Sin-wave speed multiplier on `Date.now()` — ≈1.5 round trips
 *  per second so the motion reads as a deliberate "tap tap tap" hint
 *  rather than a frantic shake. */
const ARROW_YOYO_SPEED     = 0.005;
/** Half-extent of the chevron triangle in local px (before
 *  rotation). */
const ARROW_SIZE           = 22;
const ARROW_FILL_COLOR     = 0xffffff;
const ARROW_STROKE_COLOR   = 0x1e3a8a;
const ARROW_STROKE_WIDTH   = 3;
/** Base alpha — multiplied by a gentle 0.7..1 sin pulse in
 *  lock-step with the yo-yo so the chevron also subtly brightens
 *  as it leans toward the portal. */
const ARROW_ALPHA          = 0.95;

export class SnakePortalConfetti {
    container = new Container();

    /**
     * @type {Array<{
     *   gfx: Graphics, t: number, tStep: number,
     *   spawnDist: number, startOffset: number,
     *   swirlTurns: number, swirlAmp: number, phase: number,
     *   sizeStart: number, sizeNormal: number, sizeEnd: number,
     *   portal: object,
     * }>}
     */
    _active = [];

    /** @type {Graphics[]} */
    _pool = [];

    _emitTimer = 0;

    /** @type {null | { mouthX:number, mouthY:number, inwardDx:number, inwardDy:number, axisDx:number, axisDy:number, mouthHalfWidth:number }} */
    _portal = null;

    /** Active one-shot arrival burst particles. Distinct from the
     *  continuous `_active` stream because they fly OUTWARD from the
     *  mouth instead of being sucked into it.
     *  @type {Array<{
     *    gfx: Graphics, t: number, tStep: number,
     *    startX: number, startY: number, dirX: number, dirY: number,
     *    travel: number, sizeStart: number, sizeEnd: number,
     *  }>}
     */
    _flashParticles = [];

    /** Contracting blast ring drawn during a flash. `null` when no
     *  ring is in flight. Reused — created on first flash, cleared
     *  by tweening alpha to 0. Lifetime is RING_LIFETIME_FRAMES. */
    _ringGfx = null;
    /** Ring progress 0..1; `>=1` means finished and ready to clear. */
    _ringT = 1;

    /** Persistent yo-yo "go here" chevron. Built once per portal in
     *  `setPortal(geom)` and torn down in `setPortal(null)`. Lives
     *  on TOP of the suction stream so it stays readable through
     *  the cloud. */
    _arrowGfx = null;

    /**
     * Set or clear the portal geometry. Passing non-null geometry for
     * the first time after a clear (i.e. `null → geom`) automatically
     * fires {@link flashOnAppear} so the player gets the "POP! a
     * portal opened here" blast without callers having to remember
     * to do it. Passing `null` stops new particle emission, lets the
     * in-flight stream taper off, AND cancels any in-flight arrival
     * ring.
     */
    setPortal(geom) {
        const wasNull = this._portal == null;
        this._portal = geom || null;

        if (geom) {
            // Build (or refresh) the yo-yo arrow against the latest
            // mouth orientation. `_ensureArrow` handles re-use if the
            // graphics already exists from a previous portal that
            // didn't get cleared explicitly.
            this._ensureArrow();
            if (wasNull) {
                // null → non-null transition: a brand-new portal
                // appeared on the board. Fire the arrival blast so
                // the player can't miss it.
                this.flashOnAppear();
            }
        } else {
            // Portal cleared — kill the arrow + any lingering ring.
            // Continuous-stream particles already in flight finish
            // on their own.
            this._destroyArrow();
            if (this._ringGfx) {
                if (this._ringGfx.parent) this._ringGfx.parent.removeChild(this._ringGfx);
                if (!this._ringGfx.destroyed) this._ringGfx.destroy();
                this._ringGfx = null;
            }
            this._ringT = 1;
        }
    }

    /**
     * One-shot "portal arrival" blast. Spawns a wave of radial
     * particles emanating outward from the mouth and creates a ring
     * centred on the mouth that starts large (the "blast"), then
     * shrinks down to roughly the portal mouth size and fades out
     * — gone in ~0.7s, no persistent halo left behind. Both effects
     * fade over ~0.7–0.8s. Safe to call repeatedly — a previous
     * ring is replaced and old burst particles continue to play out.
     *
     * Auto-triggered by {@link setPortal} on the null → non-null
     * transition; can also be called manually if a caller wants to
     * re-light the blast without changing the geometry.
     */
    flashOnAppear() {
        if (!this._portal) return;
        const { mouthX, mouthY, mouthHalfWidth } = this._portal;

        // Spawn the particle burst — particles fly outward in a 180°
        // arc oriented away from the wall (the inward vector points
        // INTO the wall, so the outward arc is 180° anchored on
        // -inwardDx/Dy).
        const { inwardDx, inwardDy } = this._portal;
        const outDx = -inwardDx;
        const outDy = -inwardDy;
        const baseAngle = Math.atan2(outDy, outDx);

        for (let i = 0; i < FLASH_PARTICLE_COUNT; i++) {
            const g = this._acquire();
            const sizeStart = FLASH_SIZE_MIN
                + Math.random() * (FLASH_SIZE_MAX - FLASH_SIZE_MIN);
            const color = PALETTE[Math.floor(Math.random() * PALETTE.length)];
            g.clear();
            g.rect(-0.5, -0.5, 1, 1);
            g.fill(color);
            g.scale.set(sizeStart);
            g.alpha = 0;

            // Distribute across a 180° arc anchored on the outward
            // normal so every particle visibly flies AWAY from the
            // wall instead of into it.
            const offset = (Math.random() - 0.5) * Math.PI;
            const angle = baseAngle + offset;
            const travel = FLASH_TRAVEL_MIN
                + Math.random() * (FLASH_TRAVEL_MAX - FLASH_TRAVEL_MIN);

            this._flashParticles.push({
                gfx: g,
                t: 0,
                tStep: 1 / FLASH_LIFETIME_FRAMES,
                startX: mouthX,
                startY: mouthY,
                dirX: Math.cos(angle),
                dirY: Math.sin(angle),
                travel,
                sizeStart,
                sizeEnd: sizeStart * 0.4,
            });
            this.container.addChild(g);
        }

        // Contracting blast ring — starts at the BLAST radius
        // (well outside the portal mouth) and shrinks toward the
        // mouth as it fades, reading as "energy collapsing into the
        // gate". Re-uses the existing Graphics if a ring is already
        // in flight, otherwise builds a fresh one. Sits BELOW the
        // burst particles so the particles always read on top.
        if (!this._ringGfx) {
            this._ringGfx = new Graphics();
            this.container.addChildAt(this._ringGfx, 0);
        } else if (!this._ringGfx.parent) {
            this.container.addChildAt(this._ringGfx, 0);
        }
        this._ringGfx.x = mouthX;
        this._ringGfx.y = mouthY;
        this._ringGfx.scale.set(1);
        this._ringT = 0;
        this._ringStartRadius = mouthHalfWidth * RING_BLAST_RADIUS_MULT;
        this._ringEndRadius   = mouthHalfWidth * RING_FINAL_RADIUS_MULT;
        this._drawRing(0);
    }

    /**
     * Advance emission and per-particle motion by `delta` frames (the
     * value supplied by PixiJS' ticker, ≈1 at 60 fps).
     */
    update(delta = 1) {
        if (this._portal) {
            this._emitTimer -= delta;
            let safety = 8;
            while (this._emitTimer <= 0 && safety-- > 0) {
                if (this._active.length < MAX_PARTICLES) this._spawnOne();
                this._emitTimer += EMIT_INTERVAL_FRAMES;
            }
        }

        for (let i = this._active.length - 1; i >= 0; i--) {
            const p = this._active[i];
            p.t += p.tStep * delta;
            if (p.t >= 1) {
                this._recycle(i);
                continue;
            }

            const { mouthX, mouthY, inwardDx, inwardDy, axisDx, axisDy } = p.portal;

            // Accelerating suction: most of the inward travel happens
            // in the last third of the lifecycle, so particles linger
            // briefly at spawn then "snap" into the mouth.
            const inwardOff = p.spawnDist * (1 - p.t * p.t);

            // Axial offset collapses to ~0 at the mouth (funnel), with
            // a swirl wobble overlaid that itself decays — so the
            // motion goes from "wobbly wide cloud" to "thin stream".
            const funnelFactor = Math.pow(1 - p.t, 0.85);
            const swirl = Math.sin(p.t * p.swirlTurns * Math.PI * 2 + p.phase)
                * p.swirlAmp * (1 - p.t * 0.7);
            const axialOff = p.startOffset * funnelFactor + swirl;

            p.gfx.x = mouthX - inwardDx * inwardOff + axisDx * axialOff;
            p.gfx.y = mouthY - inwardDy * inwardOff + axisDy * axialOff;

            // Two-stage size curve so the particle reads as "spawned
            // chunky and far" but rides the original "normal" size
            // for the bulk of its travel:
            //   • t ∈ [0, SIZE_SNAP_T]  → sizeStart  → sizeNormal  (fast)
            //   • t ∈ [SIZE_SNAP_T, 1]  → sizeNormal → sizeEnd     (slow,
            //                                                       quadratic)
            // Avoids the previous "way too large" stream where
            // particles stayed chunky deep into their travel.
            let size;
            if (p.t < SIZE_SNAP_T) {
                const lt = p.t / SIZE_SNAP_T;
                size = p.sizeStart + (p.sizeNormal - p.sizeStart) * lt;
            } else {
                const lt = (p.t - SIZE_SNAP_T) / (1 - SIZE_SNAP_T);
                const eased = lt * lt;
                size = p.sizeNormal + (p.sizeEnd - p.sizeNormal) * eased;
            }
            p.gfx.scale.set(size);

            // Brief fade-in (no harsh popping) + fade-out only in the
            // last 12% — the particle disappears INTO the mouth.
            let alpha;
            if (p.t < 0.08) alpha = p.t / 0.08;
            else if (p.t > 0.88) alpha = (1 - p.t) / 0.12;
            else alpha = 1;
            p.gfx.alpha = Math.max(0, alpha);
        }

        // ── Arrival flash particles (one-shot outward burst) ─────
        for (let i = this._flashParticles.length - 1; i >= 0; i--) {
            const p = this._flashParticles[i];
            p.t += p.tStep * delta;
            if (p.t >= 1) {
                this._recycleFlash(i);
                continue;
            }

            // Ease-out distance — particles cover most of their
            // travel early on and drift the rest of the way, matching
            // the firework / pop arrival vocabulary.
            const eased = 1 - Math.pow(1 - p.t, 2);
            const dist = p.travel * eased;
            p.gfx.x = p.startX + p.dirX * dist;
            p.gfx.y = p.startY + p.dirY * dist;

            const size = p.sizeStart + (p.sizeEnd - p.sizeStart) * p.t;
            p.gfx.scale.set(Math.max(0.01, size));

            // Snappy fade-in over the first 8%, linear decay through
            // the rest — looks more "pop" than "drift".
            let alpha;
            if (p.t < 0.08) alpha = p.t / 0.08;
            else alpha = 1 - (p.t - 0.08) / 0.92;
            p.gfx.alpha = Math.max(0, alpha);
        }

        // ── Contracting arrival ring ─────────────────────────────
        if (this._ringGfx && this._ringT < 1) {
            this._ringT += delta / RING_LIFETIME_FRAMES;
            if (this._ringT >= 1) {
                this._ringGfx.clear();
                this._ringGfx.alpha = 0;
            } else {
                this._drawRing(this._ringT);
            }
        }

        // ── Yo-yo "go here" arrow ────────────────────────────────
        // Position oscillates between FAR_OFFSET and NEAR_OFFSET on
        // a sin wave so the chevron visibly leans toward the gate
        // and back. Alpha mirrors the same wave so the brightness
        // peaks at the closest pose, reinforcing the "go this way"
        // beat. Re-anchored every frame in case the portal moves.
        if (this._arrowGfx && this._portal) {
            const { mouthX, mouthY, inwardDx, inwardDy } = this._portal;
            const wave = Math.sin(Date.now() * ARROW_YOYO_SPEED);
            const t01 = (wave + 1) * 0.5;
            // wave==-1 → FAR_OFFSET; wave==+1 → NEAR_OFFSET (closer
            // to the portal mouth).
            const offset = ARROW_FAR_OFFSET
                + (ARROW_NEAR_OFFSET - ARROW_FAR_OFFSET) * t01;
            // The OUTWARD normal sits at -inwardD*. Multiplying by
            // -offset places the arrow that many px INSIDE the
            // playfield from the mouth — i.e. on the side the snake
            // is approaching from.
            this._arrowGfx.x = mouthX - inwardDx * offset;
            this._arrowGfx.y = mouthY - inwardDy * offset;
            // Alpha brightens as the arrow nears the mouth (0.7..1
            // of base alpha) so the chevron also "winks" toward the
            // portal in lock-step with the yo-yo.
            this._arrowGfx.alpha = ARROW_ALPHA * (0.7 + 0.3 * t01);
        }
    }

    /** Return all active particles to the pool immediately. Also
     *  clears the one-shot arrival burst + ring. The yo-yo arrow is
     *  owned by the portal lifecycle (created/destroyed in
     *  `setPortal`) so `clear()` does NOT remove it — that would
     *  make scene resets blink the arrow every transition. */
    clear() {
        while (this._active.length > 0) {
            this._recycle(this._active.length - 1);
        }
        while (this._flashParticles.length > 0) {
            this._recycleFlash(this._flashParticles.length - 1);
        }
        if (this._ringGfx) {
            this._ringGfx.clear();
            this._ringGfx.alpha = 0;
        }
        this._ringT = 1;
        this._emitTimer = 0;
    }

    destroy() {
        this.clear();
        this._destroyArrow();
        if (this._ringGfx) {
            if (!this._ringGfx.destroyed) this._ringGfx.destroy();
            this._ringGfx = null;
        }
        for (const g of this._pool) {
            if (!g.destroyed) g.destroy();
        }
        this._pool.length = 0;
        if (!this.container.destroyed) {
            this.container.destroy({ children: true });
        }
    }

    // ── internals ─────────────────────────────────────────

    _spawnOne() {
        if (!this._portal) return;
        const g = this._acquire();

        const lifeFrames = LIFETIME_FRAMES_MIN
            + Math.random() * (LIFETIME_FRAMES_MAX - LIFETIME_FRAMES_MIN);
        // Spawn size — chunky so the particle reads at SPAWN_DIST.
        const sizeStart = SIZE_START_MIN
            + Math.random() * (SIZE_START_MAX - SIZE_START_MIN);
        // "Normal" size for the bulk of the travel — matches the
        // legacy SIZE_MIN/SIZE_MAX so the cloud looks like the
        // original after the first ~18% of the lifetime.
        const sizeNormal = SIZE_NORMAL_MIN
            + Math.random() * (SIZE_NORMAL_MAX - SIZE_NORMAL_MIN);
        const color = PALETTE[Math.floor(Math.random() * PALETTE.length)];

        // Draw at unit-square and scale via .scale.set in update — keeps
        // the geometry rebuild out of the hot path.
        g.clear();
        g.rect(-0.5, -0.5, 1, 1);
        g.fill(color);
        g.scale.set(sizeStart);
        g.alpha = 0;

        const spread = this._portal.mouthHalfWidth * SPREAD_FACTOR;
        const startOffset = (Math.random() - 0.5) * 2 * spread;

        this._active.push({
            gfx: g,
            portal: this._portal,
            t: 0,
            tStep: 1 / lifeFrames,
            spawnDist: SPAWN_DIST_MIN + Math.random() * (SPAWN_DIST_MAX - SPAWN_DIST_MIN),
            startOffset,
            swirlTurns: SWIRL_TURNS_MIN + Math.random() * (SWIRL_TURNS_MAX - SWIRL_TURNS_MIN),
            swirlAmp: SWIRL_AMP_MIN + Math.random() * (SWIRL_AMP_MAX - SWIRL_AMP_MIN),
            // Random phase + random sign means roughly half the swirl
            // wobbles clockwise and half counter — looks more organic
            // than a strict mass-rotation.
            phase: Math.random() * Math.PI * 2,
            sizeStart,
            sizeNormal,
            sizeEnd: SIZE_END,
        });
        this.container.addChild(g);
    }

    _recycle(i) {
        const p = this._active[i];
        if (p.gfx.parent) p.gfx.parent.removeChild(p.gfx);
        p.gfx.alpha = 0;
        this._pool.push(p.gfx);
        this._active.splice(i, 1);
    }

    _recycleFlash(i) {
        const p = this._flashParticles[i];
        if (p.gfx.parent) p.gfx.parent.removeChild(p.gfx);
        p.gfx.alpha = 0;
        this._pool.push(p.gfx);
        this._flashParticles.splice(i, 1);
    }

    _acquire() {
        return this._pool.pop() || new Graphics();
    }

    /**
     * Re-draws the contracting arrival ring at progress `t01` (0..1).
     * Radius lerps from `_ringStartRadius` (the BLAST radius, well
     * outside the mouth) DOWN to `_ringEndRadius` (just outside the
     * mouth itself). Alpha fades from `RING_START_ALPHA` to 0 with a
     * gentle ease-out so the ring lingers long enough to register
     * but doesn't loiter — gone in ~0.7s.
     */
    _drawRing(t01) {
        if (!this._ringGfx) return;
        const r = this._ringStartRadius
            + (this._ringEndRadius - this._ringStartRadius) * t01;
        const alpha = RING_START_ALPHA * (1 - t01);
        this._ringGfx.clear();
        this._ringGfx.circle(0, 0, r);
        this._ringGfx.stroke({
            color: 0xffffff,
            width: RING_LINE_WIDTH,
            alpha: 1,
        });
        this._ringGfx.alpha = Math.max(0, alpha);
    }

    /**
     * Build (or refresh) the yo-yo "go here" chevron Graphics. The
     * chevron is drawn pointing at the local +X axis, then rotated
     * to align with the portal's INWARD vector so its tip always
     * points at the mouth — works for portals on any side (left,
     * right, top, bottom). Position is updated every frame in
     * {@link update}; this method only owns the shape + rotation.
     *
     * Drawn ABOVE the suction stream + ring (added with no index
     * argument — appended last) so the chevron stays readable
     * through the cloud.
     */
    _ensureArrow() {
        if (!this._portal) return;
        if (!this._arrowGfx) {
            this._arrowGfx = new Graphics();
            this.container.addChild(this._arrowGfx);
        } else if (!this._arrowGfx.parent) {
            this.container.addChild(this._arrowGfx);
        }

        // Chevron geometry — a forward-pointing triangle with a
        // notched back so it reads as an arrowhead rather than a
        // generic triangle. Local +X is the "tip" direction.
        const a = this._arrowGfx;
        const s = ARROW_SIZE;
        a.clear();
        a.moveTo(s, 0);                  // tip
        a.lineTo(-s * 0.55, -s * 0.85);  // upper-left wing
        a.lineTo(-s * 0.20,  0);         // notch (closer to centre)
        a.lineTo(-s * 0.55,  s * 0.85);  // lower-left wing
        a.closePath();
        a.fill({ color: ARROW_FILL_COLOR, alpha: 1 });
        a.stroke({
            color: ARROW_STROKE_COLOR,
            width: ARROW_STROKE_WIDTH,
            alpha: 1,
        });

        // Aim the chevron along the inward vector so its tip points
        // INTO the wall (i.e. at the portal mouth). atan2(y, x)
        // returns the angle CCW from +X; PixiJS' `rotation` uses the
        // same convention.
        const { inwardDx, inwardDy } = this._portal;
        a.rotation = Math.atan2(inwardDy, inwardDx);
        a.alpha = ARROW_ALPHA;
    }

    _destroyArrow() {
        if (!this._arrowGfx) return;
        if (this._arrowGfx.parent) this._arrowGfx.parent.removeChild(this._arrowGfx);
        if (!this._arrowGfx.destroyed) this._arrowGfx.destroy();
        this._arrowGfx = null;
    }
}
